import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { cn } from '../cn';


export interface EditMessageModalProps {
  isEditModalOpen: boolean;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
  editMessage: string;
  setEditMessage: Dispatch<SetStateAction<string>>;
  isSaving: boolean;
  handleSaveMessage: () => Promise<void>;
  t: TFunction;
}

export default function EditMessageModal({
  isEditModalOpen,
  setIsEditModalOpen,
  editMessage,
  setEditMessage,
  isSaving,
  handleSaveMessage,
  t,
}: EditMessageModalProps) {
  return (
    <>
      {isEditModalOpen && (
        <div
          className="fixed inset-0 z-[2003] flex items-center justify-center bg-black-alpha-59 px-xl"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsEditModalOpen(false);
            }
          }}
        >
          <div
            className="relative box-border w-full max-w-[352px] rounded-xl bg-fullscreen-surface p-xl text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="m-0 mb-xl text-center [font-family:Lato,sans-serif] text-[18px] leading-[29px] font-bold tracking-[-0.747px] text-white"
            >
              {t("Edit owner message")}
            </h3>
            
            <textarea
              value={editMessage}
              onChange={(e) => setEditMessage(e.target.value)}
              maxLength={500}
              placeholder={t("Enter your message for guests (max 500 characters)")}
              enterKeyHint="done"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.currentTarget.blur();
                }
              }}
              className="mb-xl box-border min-h-[120px] w-full resize-y rounded-[5px] border border-deep-blue bg-transparent p-lg [font-family:Lato,sans-serif] text-[14px] leading-[18px] tracking-[-0.747px] text-white"
            />
            
            <div className="flex flex-wrap justify-end gap-lg">
              <button
                onClick={() => setIsEditModalOpen(false)}
                disabled={isSaving}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-pill border-none bg-white-alpha-12 px-2xl py-lg [font-family:Lato,sans-serif] text-[14px] font-normal text-white',
                  isSaving ? 'cursor-not-allowed opacity-60' : 'cursor-pointer opacity-100',
                )}
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleSaveMessage}
                disabled={isSaving}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-pill border-none bg-[image:var(--gradient-action-primary)] px-4xl py-lg [font-family:"Rubik_One",sans-serif] text-[14px] font-normal text-white shadow-action-raised',
                  isSaving ? 'cursor-not-allowed opacity-60' : 'cursor-pointer opacity-100',
                )}
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
